"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getQuoteTotals = exports.calculateTotalIncludingTaxAfterDiscount = exports.calculateTotalDiscountIncludingTax = exports.calculateTotalIncludingTax = exports.calculateTotalForTaxPercentage = exports.calculateTotalExcludingTax = exports.Tax = void 0;
var Tax;
(function (Tax) {
    Tax[Tax["NONE"] = 0] = "NONE";
    Tax[Tax["LOW"] = 0.09] = "LOW";
    Tax[Tax["HIGH"] = 0.21] = "HIGH";
})(Tax = exports.Tax || (exports.Tax = {}));
/** Returns the total for all products pre-tax.
 * Can optionally pass a parameter to get the total for a specific tax class.
 */
const calculateTotalExcludingTax = (items, taxPercentage) => {
    if (!items.length)
        return 0;
    return items
        .filter(item => (taxPercentage !== undefined ? item.product.tax === taxPercentage : true))
        .reduce((acc, curr) => acc + curr.amount * curr.retailPrice, 0);
};
exports.calculateTotalExcludingTax = calculateTotalExcludingTax;
/** Returns the total for all products pre-tax. */
const calculateTotalForTaxPercentage = (items, taxPercentage) => {
    if (!items.length)
        return 0;
    return items
        .filter(item => item.product.tax === taxPercentage)
        .reduce((acc, curr) => acc + curr.amount * curr.retailPrice * (curr.product.tax ?? 0), 0);
};
exports.calculateTotalForTaxPercentage = calculateTotalForTaxPercentage;
const calculateTotalIncludingTax = (items) => {
    if (!items.length)
        return 0;
    const totalExcludingTax = (0, exports.calculateTotalExcludingTax)(items);
    const totalZeroTax = (0, exports.calculateTotalForTaxPercentage)(items, Tax.NONE); // Yes, this will always be zero. But it helps for the overview.
    const totalLowTax = (0, exports.calculateTotalForTaxPercentage)(items, Tax.LOW);
    const totalHighTax = (0, exports.calculateTotalForTaxPercentage)(items, Tax.HIGH);
    return totalExcludingTax + totalZeroTax + totalLowTax + totalHighTax;
};
exports.calculateTotalIncludingTax = calculateTotalIncludingTax;
const calculateTotalDiscountIncludingTax = (items, discount) => {
    if (!items.length || !discount?.amount || !discount.type)
        return 0;
    if (discount.type.includes('percentage')) {
        const totalIncludingTax = (0, exports.calculateTotalIncludingTax)(items);
        return (discount.amount / 100) * totalIncludingTax;
    }
    return discount.amount;
};
exports.calculateTotalDiscountIncludingTax = calculateTotalDiscountIncludingTax;
const calculateTotalIncludingTaxAfterDiscount = (items, discount) => {
    const totalIncludingTax = (0, exports.calculateTotalIncludingTax)(items);
    const totalDiscountIncludingTax = (0, exports.calculateTotalDiscountIncludingTax)(items, discount);
    return totalIncludingTax - totalDiscountIncludingTax;
};
exports.calculateTotalIncludingTaxAfterDiscount = calculateTotalIncludingTaxAfterDiscount;
/** Returns the total amount for a specific tax class after discount.
 * First normalizes the tax classes so you can use the ratio between a specific tax class and the total.
 * Then uses this to calculate the necessary discount amount pre-tax to end up with the correct  post-tax number.
 */
const getDiscountedTotalPreTax = (items, taxPercentage, discount) => {
    const totalDiscount = (0, exports.calculateTotalDiscountIncludingTax)(items, discount);
    const totalZeroExcludingTax = (0, exports.calculateTotalExcludingTax)(items, Tax.NONE);
    const totalLowExcludingTax = (0, exports.calculateTotalExcludingTax)(items, Tax.LOW);
    const totalHighExcludingTax = (0, exports.calculateTotalExcludingTax)(items, Tax.HIGH);
    if (totalDiscount === 0) {
        switch (taxPercentage) {
            case Tax.HIGH:
                return totalHighExcludingTax;
            case Tax.LOW:
                return totalLowExcludingTax;
            case Tax.NONE:
                return totalZeroExcludingTax;
        }
    }
    // Normalize for ratios
    const normalizedZeroTax = totalZeroExcludingTax / (1 + Tax.NONE);
    const normalizedLowTax = totalLowExcludingTax / (1 + Tax.LOW);
    const normalizedHighTax = totalHighExcludingTax / (1 + Tax.HIGH);
    const normalizedTotalTax = normalizedZeroTax + normalizedLowTax + normalizedHighTax;
    switch (taxPercentage) {
        case Tax.HIGH: {
            const highTaxRatio = normalizedHighTax / normalizedTotalTax;
            const highDiscount = (totalDiscount * highTaxRatio) / (1 + Tax.HIGH);
            return totalHighExcludingTax - highDiscount;
        }
        case Tax.LOW: {
            const lowTaxRatio = normalizedLowTax / normalizedTotalTax;
            const lowDiscount = (totalDiscount * lowTaxRatio) / (1 + Tax.LOW);
            return totalLowExcludingTax - lowDiscount;
        }
        case Tax.NONE: {
            const zeroTaxRatio = normalizedZeroTax / normalizedTotalTax;
            const zeroTaxDiscount = totalDiscount * zeroTaxRatio; // No need to divide by 1
            return totalZeroExcludingTax - zeroTaxDiscount;
        }
        default:
            return 0;
    }
};
function getQuoteTotals(items, discount) {
    const totalLowExcludingTax = (0, exports.calculateTotalExcludingTax)(items, Tax.LOW);
    const totalHighExcludingTax = (0, exports.calculateTotalExcludingTax)(items, Tax.HIGH);
    const totalZeroExcludingTax = (0, exports.calculateTotalExcludingTax)(items, Tax.NONE);
    const totalExcludingTax = totalZeroExcludingTax + totalLowExcludingTax + totalHighExcludingTax;
    const totalZeroTax = (0, exports.calculateTotalForTaxPercentage)(items, Tax.NONE); // Yes, this will always be zero. But it helps for the overview.
    const totalLowTax = (0, exports.calculateTotalForTaxPercentage)(items, Tax.LOW);
    const totalHighTax = (0, exports.calculateTotalForTaxPercentage)(items, Tax.HIGH);
    const totalIncludingTax = totalExcludingTax + totalZeroTax + totalLowTax + totalHighTax;
    const totalDiscountIncludingTax = (0, exports.calculateTotalDiscountIncludingTax)(items, discount);
    const totalIncludingTaxAfterDiscount = totalIncludingTax - totalDiscountIncludingTax;
    const totalZeroTaxAfterDiscount = getDiscountedTotalPreTax(items, Tax.NONE, discount);
    const totalLowAfterDiscount = getDiscountedTotalPreTax(items, Tax.LOW, discount);
    const totalHighAfterDiscount = getDiscountedTotalPreTax(items, Tax.HIGH, discount);
    const totalLowTaxAfterDiscount = totalLowAfterDiscount * Tax.LOW;
    const totalHighTaxAfterDiscount = totalHighAfterDiscount * Tax.HIGH;
    const totalZeroTaxDiscount = totalZeroExcludingTax - totalZeroTaxAfterDiscount;
    const totalLowDiscount = totalLowExcludingTax - totalLowAfterDiscount;
    const totalHighDiscount = totalHighExcludingTax - totalHighAfterDiscount;
    const totalDiscountExcludingTax = totalLowDiscount + totalHighDiscount + totalZeroTaxDiscount;
    const totalExcludingTaxAfterDiscount = totalExcludingTax - totalDiscountExcludingTax;
    return {
        // Regular totals
        totalLowExcludingTax,
        totalHighExcludingTax,
        totalExcludingTax,
        totalLowTax,
        totalHighTax,
        totalIncludingTax,
        // Netto discount (after tax)
        totalZeroTaxDiscount,
        totalLowDiscount,
        totalHighDiscount,
        totalDiscountIncludingTax,
        totalIncludingTaxAfterDiscount,
        // Reverse calculations for discounted prices before tax
        totalZeroTaxAfterDiscount,
        totalLowAfterDiscount,
        totalHighAfterDiscount,
        totalLowTaxAfterDiscount,
        totalHighTaxAfterDiscount,
        totalExcludingTaxAfterDiscount,
        totalDiscountExcludingTax,
    };
}
exports.getQuoteTotals = getQuoteTotals;
